import React, {useState} from 'react';
import dashboardGraphic from '../../assets/dashboard/dashboard-graphic.jpg';
import myLabsLogo from '../../assets/mylabs-small-logo.png';

interface MembershipLockProps {
    showPdfDownload?: boolean;
    onPdfDownload?: () => void;
}

const MembershipLock: React.FC<MembershipLockProps> = ({showPdfDownload = false, onPdfDownload}) => {
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);

    // Disable body scrolling but allow modal scrolling
    React.useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleSignUp = () => {
        // Navigate to sign up page or external link
        window.open('https://mylabsdirect.com/product/my-labs-membership', '_blank');
    };

    const handlePdfDownload = async () => {
        if (isPdfDownloading || !onPdfDownload) return;

        setIsPdfDownloading(true);
        try {
            await onPdfDownload();
        } finally {
            // Reset downloading state after a delay to allow for download completion
            setTimeout(() => {
                setIsPdfDownloading(false);
            }, 2000);
        }
    };

    return (
        <div className={`absolute inset-0 z-40 flex items-start justify-center pointer-events-auto overflow-y-auto`}>
            <div className="relative max-w-md w-full mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden my-4">
                {/* Header with responsive spacing */}
                <div className="bg-white text-center pt-8 lg:pt-16">
                    <div className="mb-4 lg:mb-8">
                        <img src={myLabsLogo} alt="MyLabs" className="h-10 lg:h-12 mx-auto" />
                    </div>
                    <div className="px-4 lg:px-6 mb-4 lg:mb-6">
                        <h2
                            className="text-lg mb-1 leading-tight"
                            style={{fontFamily: 'Poppins, sans-serif', color: '#013B4B'}}>
                            Become A My Labs Member and
                        </h2>
                        <p className="text-lg" style={{fontFamily: 'Poppins, sans-serif', color: '#013B4B'}}>
                            Unlock In-Depth Digital Health Insights
                        </p>
                    </div>
                </div>

                {/* Dashboard Graphic - Full width, larger to match Figma proportions */}
                <div className="w-full">
                    <img src={dashboardGraphic} alt="Dashboard Preview" className="w-full h-auto block" />
                </div>

                {/* Features - Responsive spacing */}
                <div className="px-4 lg:px-6 py-4 lg:py-6 text-center">
                    <p
                        className="text-sm leading-relaxed mb-4"
                        style={{fontFamily: 'Poppins, sans-serif', color: '#97A5AD'}}>
                        Advanced health metrics, lab insights, wellness coaching, exclusive member discounts, and more
                    </p>

                    {/* Pricing - Integrated with features section */}
                    <p className="text-xl mb-1" style={{fontFamily: 'Poppins, sans-serif', color: '#013B4B'}}>
                        Only $99.00 Annually
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="px-4 lg:px-6 pb-6 lg:pb-8 space-y-3 flex flex-col items-center">
                    <button
                        onClick={handleSignUp}
                        className="bg-mld-background py-3 px-6 rounded-full flex items-center justify-center hover:bg-opacity-80 transition-colors"
                        style={{fontFamily: 'Poppins, sans-serif', color: '#013B4B'}}>
                        Sign Up Today
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {showPdfDownload && onPdfDownload && (
                        <button
                            onClick={handlePdfDownload}
                            disabled={isPdfDownloading}
                            className={`py-3 px-6 rounded-full transition-colors flex items-center justify-center ${
                                isPdfDownloading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-mld-background hover:bg-opacity-80'
                            }`}
                            style={{
                                fontFamily: 'Poppins, sans-serif',
                                color: isPdfDownloading ? undefined : '#013B4B',
                            }}>
                            {isPdfDownloading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                                    Downloading...
                                </>
                            ) : (
                                'Download PDF Results'
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MembershipLock;
